import { z } from "zod";

import {
  authenticatedUserSchema,
  newPasswordSchema,
  normalizedEmailSchema,
} from "./auth.js";
import { companyTypeSchema } from "./company.js";
import {
  companyManagerRoles,
  companyManagerRoleSchema,
  companyMembershipSchema,
} from "./company-member.js";

export const companyInvitationRoles = companyManagerRoles;
export const companyInvitationRoleSchema = companyManagerRoleSchema;
export const companyInvitationStatusSchema = z.enum([
  "pending",
  "accepted",
  "revoked",
  "expired",
]);

export const companyInvitationCreateRequestSchema = z.object({
  email: normalizedEmailSchema,
  role: companyInvitationRoleSchema,
});

export const companyInvitationSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  companyDisplayName: z.string().min(1),
  companyType: companyTypeSchema,
  email: z.string().email(),
  role: companyInvitationRoleSchema,
  status: companyInvitationStatusSchema,
  expiresAt: z.string().datetime(),
  acceptedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});

const invitationPersonNameSchema = z.string().trim().min(2).max(100);

function isValidTimeZone(value: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value }).format();
    return true;
  } catch {
    return false;
  }
}

export const companyInvitationAcceptRequestSchema = z.discriminatedUnion("mode", [
  z.object({ mode: z.literal("existing") }),
  z.object({
    mode: z.literal("new"),
    firstName: invitationPersonNameSchema,
    lastName: invitationPersonNameSchema,
    timezone: z.string().trim().min(1).max(64).refine(isValidTimeZone, "Invalid IANA timezone"),
    password: newPasswordSchema,
    termsAccepted: z.literal(true),
    privacyAccepted: z.literal(true),
    personalDataAccepted: z.literal(true),
    marketingAccepted: z.boolean().default(false),
  }),
]);

export const companyInvitationAcceptResponseSchema = z.object({
  user: authenticatedUserSchema,
  membership: companyMembershipSchema,
  accountCreated: z.boolean(),
});

export type CompanyInvitationRole = z.infer<typeof companyInvitationRoleSchema>;
export type CompanyInvitationCreateRequest = z.infer<
  typeof companyInvitationCreateRequestSchema
>;
export type CompanyInvitation = z.infer<typeof companyInvitationSchema>;
export type CompanyInvitationAcceptRequest = z.infer<
  typeof companyInvitationAcceptRequestSchema
>;
export type CompanyInvitationAcceptResponse = z.infer<
  typeof companyInvitationAcceptResponseSchema
>;
